import {
    CalendarOutlined,
    DollarOutlined,
    DownOutlined,
    PercentageOutlined,
    ReloadOutlined,
    RightOutlined,
    SearchOutlined,
    TagOutlined
} from "@ant-design/icons";
import {
    Badge,
    Button,
    Card,
    Col,
    Collapse,
    Divider,
    Empty,
    Input,
    List,
    Row,
    Space,
    Spin,
    Statistic,
    Table,
    Tag,
    Tooltip,
    Typography
} from "antd";
import React, { useCallback, useEffect, useState } from "react";
import { getAllActiveCampaigns, resetDatabase, searchCampaigns } from "../../services/campaignService";
import { syncCampaigns } from "../../services/campaignSyncService";
import "./CampaignsStyles.css";

const { Title, Text } = Typography;
const { Search } = Input;
const { Panel } = Collapse;

const CampaignsList = () => {
    const [campaignsData, setCampaignsData] = useState([]);
    const [allCampaignsData, setAllCampaignsData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expandedRowKeys, setExpandedRowKeys] = useState([]);
    const isMobile = window.innerWidth < 768;

    // Función para cargar campañas iniciales
    const loadInitialCampaigns = useCallback(async () => {
        try {
            console.log("Cargando campañas...");
            setLoading(true);

            await syncCampaigns();

            // Obtener campañas desde la base de datos local
            const data = await getAllActiveCampaigns();
            setAllCampaignsData(data);
            setCampaignsData(data);
        } catch (error) {
            console.error("Error al cargar campañas:", error);
            window.messageApi.error("Error al cargar campañas: " + error.message);
        } finally {
            setLoading(false);
        }
    }, []);

    // Cargar campañas al inicio
    useEffect(() => {
        loadInitialCampaigns();
    }, [loadInitialCampaigns]);

    // Función para forzar actualización desde el servidor
    const forceRefresh = useCallback(async () => {
        try {
            setLoading(true);
            console.log("Forzando actualización desde Business Central...");

            // Forzar sincronización
            const syncResult = await syncCampaigns();
            if (syncResult) {
                // Obtener campañas actualizadas
                const data = await getAllActiveCampaigns();
                setAllCampaignsData(data);
                setCampaignsData(data);
                window.messageApi.success("Campañas actualizadas correctamente");
            } else {
                window.messageApi.warning("No se pudo conectar al servidor, mostrando datos locales");
                // Intentar obtener datos locales
                const localData = await getAllActiveCampaigns();
                setAllCampaignsData(localData);
                setCampaignsData(localData);
            }
        } catch (error) {
            window.messageApi.error("Error al actualizar campañas: " + error.message);
        } finally {
            setLoading(false);
        }
    }, []);

    // Función para manejar la búsqueda
    const handleSearch = async (value) => {
        try {
            setLoading(true);
            let filteredData = allCampaignsData;

            if (value) {
                filteredData = await searchCampaigns(value);
            }

            setCampaignsData(filteredData);
        } catch (error) {
            window.messageApi.error("Error al buscar campañas: " + error.message);
        } finally {
            setLoading(false);
        }
    };

    // Función para formatear fechas
    const formatDate = (dateString) => {
        if (!dateString) return "-";
        try {
            // El formato viene como DD/MM/YY, necesitamos convertirlo
            const parts = dateString.split('/');
            if (parts.length === 3) {
                const day = parts[0];
                const month = parts[1];
                let year = parts[2];

                // Si el año es de 2 dígitos, convertir a 4 dígitos
                if (year.length === 2) {
                    // Asumimos que años 00-30 son 2000-2030, y 31-99 son 1931-1999
                    const yearNum = parseInt(year);
                    year = yearNum <= 30 ? `20${year}` : `19${year}`;
                }

                // Crear fecha en formato YYYY-MM-DD para que Date() la interprete correctamente
                const isoDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
                const date = new Date(isoDate);

                // Verificar que la fecha es válida
                if (!isNaN(date.getTime())) {
                    return date.toLocaleDateString('es-ES');
                }
            }

            // Si no se puede parsear, devolver el string original
            return dateString;
        } catch {
            return dateString;
        }
    };

    // Función para determinar si una campaña está activa
    const isCampaignActive = (campaign) => {
        const today = new Date();

        // Si no hay fechas definidas, se considera activa
        if (!campaign.Starting_Date && !campaign.Ending_Date) {
            return true;
        }

        // Verificar si está en el rango de fechas
        let startDate = null;
        let endDate = null;

        if (campaign.Starting_Date) {
            const parts = campaign.Starting_Date.split('/');
            if (parts.length === 3) {
                const day = parseInt(parts[0]);
                const month = parseInt(parts[1]) - 1;
                let year = parseInt(parts[2]);
                if (year < 100) {
                    year = year <= 30 ? 2000 + year : 1900 + year;
                }
                startDate = new Date(year, month, day);
            }
        }

        if (campaign.Ending_Date) {
            const parts = campaign.Ending_Date.split('/');
            if (parts.length === 3) {
                const day = parseInt(parts[0]);
                const month = parseInt(parts[1]) - 1;
                let year = parseInt(parts[2]);
                if (year < 100) {
                    year = year <= 30 ? 2000 + year : 1900 + year;
                }
                endDate = new Date(year, month, day);
            }
        }

        if (startDate && today < startDate) return false;
        if (endDate && today > endDate) return false;

        return true;
    };

    // Componente para mostrar los detalles expandidos
    const expandedRowRender = (record) => {
        let salesPrices = [];
        let lineDiscounts = [];

        try {
            salesPrices = record.Sales_Prices ? JSON.parse(record.Sales_Prices) : [];
            lineDiscounts = record.Line_Discounts ? JSON.parse(record.Line_Discounts) : [];
        } catch (error) {
            console.error("Error al parsear datos de campaña:", error);
        }

        return (
            <div style={{ padding: '16px', backgroundColor: '#fafafa' }}>
                <Collapse defaultActiveKey={['info']} ghost>
                    {/* Información de la campaña */}
                    <Panel
                        header={
                            <Space>
                                <TagOutlined />
                                <strong>Información de la Campaña</strong>
                            </Space>
                        }
                        key="info"
                    >
                        <Row gutter={16}>
                            <Col span={8}>
                                <Statistic
                                    title="Número de Campaña"
                                    value={record.No}
                                    prefix={<TagOutlined />}
                                />
                            </Col>
                            <Col span={8}>
                                <Statistic
                                    title="Estado"
                                    value={isCampaignActive(record) ? "Activa" : "Inactiva"}
                                    valueStyle={{
                                        color: isCampaignActive(record) ? '#3f8600' : '#cf1322'
                                    }}
                                />
                            </Col>
                            <Col span={8}>
                                <Statistic
                                    title="Precios de Venta"
                                    value={salesPrices.length}
                                    prefix={<DollarOutlined />}
                                />
                            </Col>
                        </Row>
                    </Panel>

                    {/* Precios de Venta */}
                    {salesPrices.length > 0 && (
                        <Panel
                            header={
                                <Space>
                                    <DollarOutlined />
                                    <strong>Precios de Venta ({salesPrices.length})</strong>
                                </Space>
                            }
                            key="prices"
                        >
                            <List
                                dataSource={salesPrices}
                                renderItem={(price, index) => (
                                    <List.Item key={index}>
                                        <List.Item.Meta
                                            title={
                                                <Space>
                                                    <Text strong>Producto: {price.Item_No}</Text>
                                                    <Text>Precio: {price.Unit_Price}€</Text>
                                                </Space>
                                            }
                                            description={
                                                <Space>
                                                    <Text>Desde: {formatDate(price.Starting_Date)}</Text>
                                                    <Text>Hasta: {formatDate(price.Ending_Date)}</Text>
                                                    {price.Minimum_Quantity > 0 && (
                                                        <Text>Cantidad mín: {price.Minimum_Quantity}</Text>
                                                    )}
                                                </Space>
                                            }
                                        />
                                    </List.Item>
                                )}
                            />
                        </Panel>
                    )}

                    {/* Descuentos por Línea */}
                    {lineDiscounts.length > 0 && (
                        <Panel
                            header={
                                <Space>
                                    <PercentageOutlined />
                                    <strong>Descuentos por Línea ({lineDiscounts.length})</strong>
                                </Space>
                            }
                            key="discounts"
                        >
                            <List
                                dataSource={lineDiscounts}
                                renderItem={(discount, index) => (
                                    <List.Item key={index}>
                                        <List.Item.Meta
                                            title={
                                                <Space>
                                                    <Text strong>{discount.Type}: {discount.Code}</Text>
                                                    <Text>{discount.Line_Discount_Percentage}% descuento</Text>
                                                </Space>
                                            }
                                            description={
                                                <Space>
                                                    <Text>Desde: {formatDate(discount.Starting_Date)}</Text>
                                                    <Text>Hasta: {formatDate(discount.Ending_Date)}</Text>
                                                    {discount.Minimum_Quantity > 0 && (
                                                        <Text>Cantidad mín: {discount.Minimum_Quantity}</Text>
                                                    )}
                                                </Space>
                                            }
                                        />
                                    </List.Item>
                                )}
                            />
                        </Panel>
                    )}
                </Collapse>
            </div>
        );
    };

    const columns = [
        {
            title: "",
            key: "expand",
            width: 50,
            render: (_, record) => {
                let hasSalesPrices = false;
                let hasLineDiscounts = false;

                try {
                    const salesPrices = record.Sales_Prices ? JSON.parse(record.Sales_Prices) : [];
                    const lineDiscounts = record.Line_Discounts ? JSON.parse(record.Line_Discounts) : [];
                    hasSalesPrices = salesPrices.length > 0;
                    hasLineDiscounts = lineDiscounts.length > 0;
                } catch (error) {
                    console.error("Error al verificar detalles de campaña:", error);
                }

                const hasDetails = hasSalesPrices || hasLineDiscounts;

                if (!hasDetails) return null;

                const isExpanded = expandedRowKeys.includes(record.No);
                return (
                    <Button
                        type="text"
                        size="small"
                        icon={isExpanded ? <DownOutlined /> : <RightOutlined />}
                        onClick={() => {
                            const newExpandedKeys = isExpanded
                                ? expandedRowKeys.filter(key => key !== record.No)
                                : [...expandedRowKeys, record.No];
                            setExpandedRowKeys(newExpandedKeys);
                        }}
                    />
                );
            },
        },
        {
            title: "Número",
            dataIndex: "No",
            key: "No",
            width: 120,
            fixed: isMobile ? false : "left",
            render: (text) => (
                <Space>
                    <TagOutlined style={{ color: "#1890ff" }} />
                    <strong>{text || "-"}</strong>
                </Space>
            ),
        },
        {
            title: "Descripción",
            dataIndex: "Description",
            key: "Description",
            width: 250,
            render: (text) => (
                <span style={{ fontWeight: 500 }}>{text || "Sin descripción"}</span>
            ),
        },
        {
            title: "Estado",
            key: "Status",
            width: 100,
            render: (_, record) => {
                const isActive = isCampaignActive(record);
                return (
                    <Badge
                        status={isActive ? "processing" : "default"}
                        text={isActive ? "Activa" : "Inactiva"}
                    />
                );
            },
        },
        {
            title: "Fecha Inicio",
            dataIndex: "Starting_Date",
            key: "Starting_Date",
            width: 120,
            render: (date) => (
                <Space>
                    <CalendarOutlined />
                    {formatDate(date)}
                </Space>
            ),
        },
        {
            title: "Fecha Fin",
            dataIndex: "Ending_Date",
            key: "Ending_Date",
            width: 120,
            render: (date) => (
                <Space>
                    <CalendarOutlined />
                    {formatDate(date)}
                </Space>
            ),
        },
        {
            title: "Precios",
            key: "salesPricesCount",
            width: 80,
            render: (_, record) => {
                try {
                    const salesPrices = record.Sales_Prices ? JSON.parse(record.Sales_Prices) : [];
                    return salesPrices.length > 0 ? (
                        <Tag color="blue">{salesPrices.length}</Tag>
                    ) : "-";
                } catch {
                    return "-";
                }
            },
        },
        {
            title: "Descuentos",
            key: "lineDiscountsCount",
            width: 100,
            render: (_, record) => {
                try {
                    const lineDiscounts = record.Line_Discounts ? JSON.parse(record.Line_Discounts) : [];
                    return lineDiscounts.length > 0 ? (
                        <Tag color="green">{lineDiscounts.length}</Tag>
                    ) : "-";
                } catch {
                    return "-";
                }
            },
        },
    ];

    // Calcular estadísticas
    const stats = {
        total: campaignsData.length,
        active: campaignsData.filter(c => isCampaignActive(c)).length,
        inactive: campaignsData.filter(c => !isCampaignActive(c)).length,
    };

    if (loading && campaignsData.length === 0) {
        return (
            <div style={{ textAlign: "center", padding: "50px" }}>
                <Spin size="large" />
                <p style={{ marginTop: "16px" }}>Cargando campañas activas...</p>
            </div>
        );
    }

    return (
        <div className={isMobile ? "campaigns-mobile" : "campaigns-container"}>
            <Card>
                <Row justify="space-between" align="middle" style={{ marginBottom: "24px" }}>
                    <Col>
                        <Title level={3} style={{ margin: 0 }}>
                            Campañas
                        </Title>
                    </Col>
                    <Col>
                        <Space>
                            <Tooltip title="Actualizar desde servidor">
                                <Button
                                    icon={<ReloadOutlined />}
                                    onClick={forceRefresh}
                                    loading={loading}
                                >
                                    Actualizar
                                </Button>
                            </Tooltip>
                            <Tooltip title="Resetear base de datos (para resolver errores)">
                                <Button
                                    danger
                                    onClick={() => {
                                        if (window.confirm("¿Estás seguro de que quieres resetear la base de datos? Se perderán todos los datos locales.")) {
                                            resetDatabase();
                                        }
                                    }}
                                >
                                    Reset DB
                                </Button>
                            </Tooltip>
                        </Space>
                    </Col>
                </Row>

                {/* Estadísticas */}
                <Row gutter={16} style={{ marginBottom: "24px" }}>
                    <Col xs={8} sm={8}>
                        <Statistic title="Total Campañas" value={stats.total} />
                    </Col>
                    <Col xs={8} sm={8}>
                        <Statistic title="Activas" value={stats.active} valueStyle={{ color: '#3f8600' }} />
                    </Col>
                    <Col xs={8} sm={8}>
                        <Statistic title="Inactivas" value={stats.inactive} valueStyle={{ color: '#cf1322' }} />
                    </Col>
                </Row>

                <Divider />

                {/* Filtros */}
                <Row gutter={16} style={{ marginBottom: "24px" }}>
                    <Col xs={24} sm={12} md={8}>
                        <Search
                            placeholder="Buscar campañas..."
                            allowClear
                            enterButton={<SearchOutlined />}
                            size="middle"
                            onSearch={handleSearch}
                            onChange={(e) => {
                                if (!e.target.value) {
                                    handleSearch("");
                                }
                            }}
                        />
                    </Col>
                </Row>

                {/* Tabla */}
                <Table
                    className="campaigns-table"
                    columns={columns}
                    dataSource={campaignsData}
                    rowKey="No"
                    expandable={{
                        expandedRowRender,
                        expandedRowKeys,
                        onExpandedRowsChange: setExpandedRowKeys,
                        expandRowByClick: false,
                        showExpandColumn: false,
                    }}
                    pagination={{
                        total: campaignsData.length,
                        pageSize: 50,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total, range) =>
                            `${range[0]}-${range[1]} de ${total} campañas`,
                    }}
                    scroll={{ x: 1000, y: isMobile ? 400 : 600 }}
                    locale={{
                        emptyText: (
                            <Empty
                                description="No hay campañas disponibles"
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                            />
                        ),
                    }}
                    size={isMobile ? "small" : "middle"}
                    loading={loading}
                />
            </Card>
        </div>
    );
};

export default CampaignsList; 